import { Controller } from "@hotwired/stimulus"

const AGGREGATE_SKELETON = `
  <div class="bg-[#FAFAFB] p-3 sm:p-5 min-h-full" data-insights-skeleton>
    <div class="w-full animate-pulse" role="status" aria-live="polite" aria-label="Generating insights">
      <div class="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
        <div class="h-7 w-56 rounded-md bg-[#E4E5E8]"></div>
        <div class="h-7 w-36 rounded-md bg-[#E4E5E8]"></div>
      </div>
      <p class="mb-6 flex items-center gap-2 text-xs text-[#6B7280]">
        <span class="w-3.5 h-3.5 rounded-full border-2 border-[#7147FF] border-t-transparent animate-spin" aria-hidden="true"></span>
        Generating fresh insights from participant conversations — content will fill in automatically.
      </p>
      <div class="bg-white rounded-lg border border-[#E4E5E8] p-5 mb-8">
        <div class="h-5 w-44 rounded bg-[#E4E5E8] mb-3"></div>
        <div class="h-3 w-full rounded bg-[#F2F3F5] mb-2"></div>
        <div class="h-3 w-11/12 rounded bg-[#F2F3F5] mb-2"></div>
        <div class="h-3 w-2/3 rounded bg-[#F2F3F5]"></div>
      </div>
      ${[0, 1, 2].map(() => `
        <div class="bg-white rounded-lg border border-[#E4E5E8] p-5 mb-4 sm:mb-6">
          <div class="flex items-center justify-between gap-3 mb-4">
            <div class="h-5 w-1/3 rounded bg-[#E4E5E8]"></div>
            <div class="flex gap-2">
              <div class="h-5 w-16 rounded-full bg-[#F2F3F5]"></div>
              <div class="h-5 w-20 rounded-full bg-[#F2F3F5]"></div>
            </div>
          </div>
          <div class="h-3 w-full rounded bg-[#F2F3F5] mb-2"></div>
          <div class="h-3 w-full rounded bg-[#F2F3F5] mb-2"></div>
          <div class="h-3 w-4/6 rounded bg-[#F2F3F5]"></div>
        </div>
      `).join("")}
    </div>
  </div>
`

const PARTICIPANT_CARD_SKELETON = `
  <div class="h-full" data-insights-skeleton>
    <div class="h-full bg-white rounded-lg border border-[#E4E5E8] p-6 animate-pulse" role="status" aria-label="Generating participant summary">
      <div class="flex items-center gap-3">
        <div class="size-10 shrink-0 rounded-full bg-[#E4E5E8]"></div>
        <div class="flex-1 min-w-0">
          <div class="h-4 w-1/2 rounded bg-[#E4E5E8] mb-2"></div>
          <div class="h-3 w-1/3 rounded bg-[#F2F3F5]"></div>
        </div>
      </div>
      <div class="mt-5 space-y-2">
        <div class="h-3 w-full rounded bg-[#F2F3F5]"></div>
        <div class="h-3 w-full rounded bg-[#F2F3F5]"></div>
        <div class="h-3 w-11/12 rounded bg-[#F2F3F5]"></div>
        <div class="h-3 w-2/3 rounded bg-[#F2F3F5]"></div>
      </div>
      <div class="mt-6 flex items-center justify-between border-t border-[#F2F3F5] pt-4">
        <div class="h-3 w-16 rounded bg-[#F2F3F5]"></div>
        <div class="h-3 w-20 rounded bg-[#F2F3F5]"></div>
      </div>
    </div>
  </div>
`

export default class extends Controller {
  static targets = ["button", "icon", "text", "buttonContent"]
  static values = {
    url: String,
    statusUrl: String,
    subjectLabel: { type: String, default: "participant" }
  }

  connect() {
    // Listen for insights:generated event from broadcast to reset button
    this.boundHandleGenerated = this.handleGenerated.bind(this)
    window.addEventListener("insights:generated", this.boundHandleGenerated)

    // Polling interval (in milliseconds)
    this.pollingInterval = null

    // Server-rendered content saved while skeleton placeholders are shown
    this.savedAggregateHtml = null
    this.savedCardsHtml = null

    // Store original button content on connect
    if (this.hasButtonContentTarget) {
      this.originalContent = this.buttonContentTarget.innerHTML
    }

    // Check if there's already a job running (in case of page refresh)
    this.checkInitialJobStatus()
  }

  disconnect() {
    window.removeEventListener("insights:generated", this.boundHandleGenerated)
    this.stopPolling()
  }

  async generate(event) {
    event.preventDefault()

    // Show loading state
    this.showLoading()

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.already) {
          this.showInfo(data.message || 'Generation is already in progress')
        }
        // Job queued successfully, show processing state and start polling
        this.showProcessingState()
        this.startPolling()
      } else {
        const error = await response.json()
        this.showError(error.error || 'Failed to generate insights')
        this.resetButton()
        this.clearSkeletons()
      }
    } catch (error) {
      console.error('Insights generation error:', error)
      this.showError('An error occurred. Please try again.')
      this.resetButton()
      this.clearSkeletons()
    }
  }

  handleGenerated() {
    // Reset button when insights are generated via broadcast
    this.stopPolling()
    this.resetButton()
    this.clearSkeletons()
  }

  startPolling() {
    // Poll every 3 seconds to check job status
    this.pollingInterval = setInterval(() => {
      this.checkJobStatus()
    }, 3000)
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  async checkJobStatus() {
    try {
      const response = await fetch(this.statusUrlValue, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()

        if (data.status === 'completed') {
          // Job finished successfully - reload page to show new insights
          this.stopPolling()
          this.resetButton()
          // Reload the page to show updated insights
          window.location.reload()
        } else if (data.status === 'idle') {
          // No job running (might have been cleared)
          this.stopPolling()
          this.resetButton()
          this.clearSkeletons()
        } else if (data.status === 'failed') {
          // Job failed in Solid Queue
          this.stopPolling()
          this.showError(data.error || 'Failed to generate insights')
          this.resetButton()
          this.clearSkeletons()
        } else if (data.status === 'error') {
          // Job failed with error
          this.stopPolling()
          this.showError(data.error || data.message || 'Failed to generate insights')
          this.resetButton()
          this.clearSkeletons()
        }
        // If status is 'processing', keep polling
      } else {
        console.error('Failed to check job status:', response.status)
      }
    } catch (error) {
      console.error('Error checking job status:', error)
      // Don't stop polling on network errors, might be temporary
    }
  }

  async checkInitialJobStatus() {
    // Check if there's a job already running when page loads
    try {
      const response = await fetch(this.statusUrlValue, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()

        if (data.status === 'processing') {
          // Job is running, show processing state and start polling
          this.showProcessingState()
          this.startPolling()
        }
      }
    } catch (error) {
      console.error('Error checking initial job status:', error)
      // Silently fail - not critical for initial load
    }
  }

  // Replace the aggregate report and participant cards with animated skeletons
  // while generation runs. Turbo broadcasts or the completion reload swap the
  // real content back in; failures restore the saved markup.
  renderSkeletons() {
    const aggregate = document.getElementById('aggregate-insights-content')
    if (aggregate && !aggregate.querySelector('[data-insights-skeleton]')) {
      if (this.savedAggregateHtml == null) this.savedAggregateHtml = aggregate.innerHTML
      aggregate.innerHTML = AGGREGATE_SKELETON.replaceAll("participant conversations", `${this.subjectLabelValue} conversations`)
    }

    const cardsGrid = document.querySelector('#participant-cards .grid')
    if (cardsGrid && !cardsGrid.querySelector('[data-insights-skeleton]')) {
      if (this.savedCardsHtml == null) this.savedCardsHtml = cardsGrid.innerHTML
      const count = Math.max(cardsGrid.children.length, 3)
      cardsGrid.innerHTML = Array.from({ length: count }, () => PARTICIPANT_CARD_SKELETON.replaceAll("participant summary", `${this.subjectLabelValue} summary`)).join('')
    }
  }

  clearSkeletons() {
    const aggregate = document.getElementById('aggregate-insights-content')
    if (aggregate && this.savedAggregateHtml != null && aggregate.querySelector('[data-insights-skeleton]')) {
      aggregate.innerHTML = this.savedAggregateHtml
    }
    this.savedAggregateHtml = null

    const cardsGrid = document.querySelector('#participant-cards .grid')
    if (cardsGrid && this.savedCardsHtml != null && cardsGrid.querySelector('[data-insights-skeleton]')) {
      cardsGrid.innerHTML = this.savedCardsHtml
    }
    this.savedCardsHtml = null
  }

  showLoading() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
    }

    // Update button content to show loading
    if (this.hasButtonContentTarget) {
      this.buttonContentTarget.innerHTML = `
        <svg class="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8.54661 19.7675C10.9457 20.8319 13.8032 20.7741 16.2502 19.3613C20.3157 17.0141 21.7086 11.8156 19.3614 7.75008L19.1114 7.31706M4.63851 16.2502C2.2913 12.1847 3.68424 6.98619 7.74972 4.63898C10.1967 3.22621 13.0542 3.16841 15.4533 4.23277M2.49341 16.3338L5.22546 17.0659L5.95751 14.3338M18.0426 9.6659L18.7747 6.93385L21.5067 7.6659"/>
        </svg>
        <span>Generating...</span>
      `
    }
  }

  showProcessingState() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
    }

    // Update button content to show processing
    if (this.hasButtonContentTarget) {
      this.buttonContentTarget.innerHTML = `
        <svg class="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8.54661 19.7675C10.9457 20.8319 13.8032 20.7741 16.2502 19.3613C20.3157 17.0141 21.7086 11.8156 19.3614 7.75008L19.1114 7.31706M4.63851 16.2502C2.2913 12.1847 3.68424 6.98619 7.74972 4.63898C10.1967 3.22621 13.0542 3.16841 15.4533 4.23277M2.49341 16.3338L5.22546 17.0659L5.95751 14.3338M18.0426 9.6659L18.7747 6.93385L21.5067 7.6659"/>
        </svg>
        <span>Processing...</span>
      `
    }

    this.renderSkeletons()
  }

  resetButton() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = false
    }

    // Restore original button content (always prefer original content to preserve server-side text)
    if (this.hasButtonContentTarget && this.originalContent) {
      this.buttonContentTarget.innerHTML = this.originalContent
    }
  }

  showError(message) {
    // Show toast notification
    const event = new CustomEvent('toast:show', {
      detail: {
        message: message,
        type: 'error'
      }
    })
    window.dispatchEvent(event)
  }

  showInfo(message) {
    const event = new CustomEvent('toast:show', {
      detail: {
        message: message,
        type: 'info'
      }
    })
    window.dispatchEvent(event)
  }
}
